'use strict';

import _ from 'lodash';
import * as crypto from 'crypto';
import {enumerate} from 'ts-transformer-enumerate';

import Item, {ItemAttributes} from '~/shared/src/ci/Item';
import {PropertyAttributes} from '~/shared/src/ci/Property';
import {ReferenceAttributes} from '~/shared/src/ci/Reference';
import {ReferencePropertyAttributes} from '~/shared/src/ci/ReferenceProperty';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import metaModel from '~/shared/src/utilities/metaModel';
import Joi from '~/shared/src/utilities/joiExtended';


/*** type definitions ************************************************************************************************/

export type DataAggregator = 'avg' | 'collect' | 'count' | 'count(*)' | 'max' | 'min' | 'sum';

export type DataStructure = DataObjectProperties & {
    dataStructure? : DataStructure[];
};

export type ArrayElementsSelector = 'every' | 'some';

export type ItemAttributesDataDescriptor = Exclude<keyof ItemAttributes, 'properties' | 'enumProperties'> | 'itemTypeName' | 'statusName';
export type ItemDataDescriptor = {
    type : 'item';

    alias : string;
    fullAlias : string;
    attribute : ItemAttributesDataDescriptor;
    attributePath : string;
    dataObject : DataStructure;
    field : string;
    label : string;

    aggregate? : DataAggregator;

    arrayElementsSelector? : never;
    fields? : never;
    propertyTypeName? : never;
    valueType? : never;
};
export type ItemsDataDescriptor = Omit<ItemDataDescriptor, 'type' | 'arrayElementsSelector'> & {
    type : 'items',

    arrayElementsSelector : ArrayElementsSelector;
};

export type PropertyAttributesDataDescriptor =
    keyof PropertyAttributes
    | 'propertySetName'
    | 'propertyTypeName'
    | 'statusName'
    | 'orderNum'
    | 'label';
export type PropertyDataDescriptor = {
    type : 'property';

    alias : string;
    fullAlias : string;
    attribute : PropertyAttributesDataDescriptor;
    attributePath : string;
    dataObject : DataStructure;
    field : string;
    label : string;
    propertyTypeName : string;

    aggregate? : DataAggregator;

    arrayElementsSelector? : never;
    fields? : never;
    valueType? : never;
};
export type PropertiesDataDescriptor = Omit<PropertyDataDescriptor, 'type' | 'arrayElementsSelector'> & {
    type : 'properties',

    arrayElementsSelector : ArrayElementsSelector;
};

export type ReferenceAttributesDataDescriptor =
    Exclude<keyof ReferenceAttributes, 'properties' | 'enumProperties'>
    | 'referenceSetName'
    | 'referenceTypeName'
    | 'referenceTypeNameWithDirection'
    | 'referenceEnd'
    | 'statusName';
export type ReferenceDataDescriptor = {
    type : 'reference';

    alias : string;
    fullAlias : string;
    attribute : ReferenceAttributesDataDescriptor;
    attributePath : string;
    dataObject : DataStructure;
    field : string;
    label : string;

    aggregate? : DataAggregator;

    arrayElementsSelector? : never;
    fields? : never;
    propertyTypeName? : never;
    valueType? : never;
};
export type ReferencesDataDescriptor = Omit<ReferenceDataDescriptor, 'type' | 'arrayElementsSelector'> & {
    type : 'references',

    arrayElementsSelector : ArrayElementsSelector;
};

export type ReferencePropertyAttributesDataDescriptor =
    keyof ReferencePropertyAttributes
    | 'referencePropertySetName'
    | 'propertyTypeName'
    | 'statusName'
    | 'orderNum'
    | 'label';
export type ReferencePropertyDataDescriptor = {
    type : 'referenceProperty';

    alias : string;
    fullAlias : string;
    attribute : ReferencePropertyAttributesDataDescriptor;
    attributePath : string;
    dataObject : DataStructure;
    field : string;
    label : string;
    propertyTypeName : string;

    aggregate? : DataAggregator;

    arrayElementsSelector? : never;
    fields? : never;
    valueType? : never;
};
export type ReferencePropertiesDataDescriptor = Omit<ReferencePropertyDataDescriptor, 'type' | 'arrayElementsSelector'> & {
    type : 'referenceProperties',

    arrayElementsSelector : ArrayElementsSelector;
};

export type ObjectDataDescriptor =
    ItemDataDescriptor
    | ItemsDataDescriptor
    | PropertyDataDescriptor
    | PropertiesDataDescriptor
    | ReferenceDataDescriptor
    | ReferencesDataDescriptor
    | ReferencePropertyDataDescriptor
    | ReferencePropertiesDataDescriptor;
export type ObjectDataDescriptorParams = Pick<ObjectDataDescriptor, 'type' | 'attribute' | 'propertyTypeName' | 'valueType'>;

export type StaticDataDescriptor = {
    type : 'static';

    alias : string;
    fullAlias : string;
    dataObject : DataStructure;
    field : string;
    valueType : PropertyValueType;

    aggregate? : never;
    arrayElementsSelector? : never;
    attribute? : never;
    attributePath? : never;
    fields? : never;
    label? : never;
    propertyTypeName? : never;
};

export type ExpressionDataDescriptor = {
    type : 'expression';

    alias : string;
    fullAlias : string;
    attributePath : string;
    dataDescriptors : ObjectDataDescriptor[];
    field : string;
    fields : string[];

    aggregate? : DataAggregator;

    attribute? : never;
    arrayElementsSelector? : never;
    dataObject? : never;
    label? : never;
    propertyTypeName? : never;
    valueType? : never;
};

export type DataDescriptor =
    ObjectDataDescriptor
    | StaticDataDescriptor
    | ExpressionDataDescriptor;

export type DataDescriptorBuildParams = {
    aggregateFunction? : DataAggregator;
    attributePath : string;
    queryFieldName? : never;
    valueType? : PropertyValueType;
} | {
    aggregateFunction? : DataAggregator;
    attributePath? : never;
    queryFieldName : string;
    valueType? : PropertyValueType;
};

export type DataDescriptors = {
    [ dataDescriptorAlias : string ] : DataDescriptor;
}

export type ReferenceConfiguration = {
    [ referenceType : string ] : {
        dontIncludeAllProperties? : boolean;
        referenceIsMandatory? : boolean;
        referenceDirection? : 'SrcToDest' | 'DestToSrc' | '*';
        referenceRecursionMin? : number;
        referenceRecursionMax? : number;
    };
};


/*** MetaModel types *************************************************************************************************/

// CFG:DataObject
export type DataObjectProperties = {
    id : string;
    origId : number;

    item : {
        id : number;
        key : string;
        name : string;

        dontIncludeAllProperties? : boolean;
        excludedProperties? : { [ propertyTypeName : string ] : '' },
        includedProperties? : { [ propertyTypeName : string ] : '' },

        label? : string;
        objectSchema? : string;
        objectType? : string;
    }

    reference? : DataObjectReferenceProperties;
    isCyclic? : boolean;

    referenceTarget? : {
        nodeId : string;
        destNodeId : string;
        referenceType : string;
        referenceDirection : 'SrcToDest' | 'DestToSrc' | '*';
        payloadCirsIds : number[],
        subMatch? : string;
        cypherMatchNum? : number;
        stopped? : boolean;
    };
}

// CFG:DataObject<--CFG:DataObject
export type DataObjectReferenceProperties = {
    id : number;

    referenceConfiguration? : ReferenceConfiguration;
};


/*** validation schemes **********************************************************************************************/

export const dataStructureSchema = Joi.object( {
    id    : Joi.string()
        .required(),
    origId: Joi.number()
        .required(),

    item: Joi.object( {
        id  : Joi.number()
            .required(),
        key : Joi.string()
            .required(),
        name: Joi.string()
            .required(),

        dontIncludeAllProperties: Joi.boolean(),

        label       : Joi.string(),
        objectSchema: Joi.string(),
        objectType  : Joi.string()
    } ).required(),

    reference: Joi.object( {
        id: Joi.number()
            .required(),

        referenceConfiguration: Joi.object()
            .pattern(
                /.*/,
                Joi.object( {
                    dontIncludeAllProperties: Joi.boolean(),
                    referenceIsMandatory    : Joi.boolean(),
                    referenceDirection      : Joi.string()
                        .valid( 'SrcToDest', 'DestToSrc', '*' ),
                    referenceRecursionMin   : Joi.number(),
                    referenceRecursionMax   : Joi.number()
                } ) )
    } ),

    isCyclic: Joi.boolean(),

    referenceTarget: Joi.object( {
        nodeId            : Joi.string()
            .required(),
        destNodeId        : Joi.string()
            .required(),
        referenceType     : Joi.string()
            .required(),
        referenceDirection: Joi.string()
            .valid( 'SrcToDest', 'DestToSrc', '*' )
            .required(),
        payloadCirsIds    : Joi.array()
            .items(
                Joi.number()
            )
            .required(),
        subMatch          : Joi.string(),
        cypherMatchNum    : Joi.number(),
        stopped           : Joi.boolean()
    } ),

    dataStructure: Joi.array()
        .items( Joi.link( '#dataStructureSchema' ) )
} ).id( 'dataStructureSchema' );

const aggregators = enumerate<DataAggregator>();
const valueTypes = enumerate<PropertyValueType>();
const itemAttributes = enumerate<ItemAttributesDataDescriptor>();
const propertyAttributes = enumerate<PropertyAttributesDataDescriptor>();
const referenceAttributes = enumerate<ReferenceAttributesDataDescriptor>();
const referencePropertyAttributes = enumerate<ReferencePropertyAttributesDataDescriptor>();

export const itemDataDescriptorSchema = Joi.object( {
    type: Joi.string()
        .valid( 'item' )
        .required(),

    alias        : Joi.string()
        .required(),
    fullAlias    : Joi.string()
        .required(),
    attribute    : Joi.string()
        .valid( ...itemAttributes )
        .required(),
    attributePath: Joi.string()
        .required(),
    dataObject   : dataStructureSchema
        .required(),
    field        : Joi.string()
        .required(),
    label        : Joi.string()
        .required(),

    aggregate: Joi.string()
        .valid( ...aggregators )
} );

export const propertyDataDescriptorSchema = Joi.object( {
    type: Joi.string()
        .valid( 'property' )
        .required(),

    alias           : Joi.string()
        .required(),
    fullAlias       : Joi.string()
        .required(),
    attribute       : Joi.string()
        .valid( ...propertyAttributes )
        .required(),
    attributePath   : Joi.string()
        .required(),
    dataObject      : dataStructureSchema
        .required(),
    field           : Joi.string()
        .required(),
    label           : Joi.string()
        .required(),
    propertyTypeName: Joi.string()
        .required(),

    aggregate: Joi.string()
        .valid( ...aggregators )
} );

export const referenceDataDescriptorSchema = Joi.object( {
    type: Joi.string()
        .valid( 'reference' )
        .required(),

    alias        : Joi.string()
        .required(),
    fullAlias    : Joi.string()
        .required(),
    attribute    : Joi.string()
        .valid( ...referenceAttributes )
        .required(),
    attributePath: Joi.string()
        .required(),
    dataObject   : dataStructureSchema
        .required(),
    field        : Joi.string()
        .required(),
    label        : Joi.string()
        .required(),

    aggregate: Joi.string()
        .valid( ...aggregators )
} );

export const referencePropertyDataDescriptorSchema = Joi.object( {
    type: Joi.string()
        .valid( 'referenceProperty' )
        .required(),

    alias           : Joi.string()
        .required(),
    fullAlias       : Joi.string()
        .required(),
    attribute       : Joi.string()
        .valid( ...referencePropertyAttributes )
        .required(),
    attributePath   : Joi.string()
        .required(),
    dataObject      : dataStructureSchema
        .required(),
    field           : Joi.string()
        .required(),
    label           : Joi.string()
        .required(),
    propertyTypeName: Joi.string()
        .required(),

    aggregate: Joi.string()
        .valid( ...aggregators )
} );

export const staticDataDescriptorSchema = Joi.object( {
    type: Joi.string()
        .valid( 'static' )
        .required(),

    alias     : Joi.string()
        .required(),
    fullAlias : Joi.string()
        .required(),
    dataObject: dataStructureSchema
        .required(),
    field     : Joi.string()
        .required(),
    valueType : Joi.string()
        .required()
        .valid( ...valueTypes )
} );

export const expressionDataDescriptorSchema = Joi.object( {
    type: Joi.string()
        .required()
        .valid( 'expression' ),

    alias          : Joi.string()
        .required(),
    fullAlias      : Joi.string()
        .required(),
    attributePath  : Joi.string()
        .required(),
    dataDescriptors: Joi.array()
        .items(
            itemDataDescriptorSchema,
            propertyDataDescriptorSchema,
            referenceDataDescriptorSchema,
            referencePropertyDataDescriptorSchema
        )
        .required(),
    field          : Joi.string()
        .required(),
    fields         : Joi.array()
        .items(
            Joi.string()
        )
        .required(),

    aggregate: Joi.string()
        .valid( ...aggregators )
} );

export const dataDescriptorSchema = Joi.alternatives()
    .try(
        itemDataDescriptorSchema,
        propertyDataDescriptorSchema,
        referenceDataDescriptorSchema,
        referencePropertyDataDescriptorSchema,
        staticDataDescriptorSchema,
        expressionDataDescriptorSchema
    );

/*** methods *********************************************************************************************************/

/**
 * Gets the dataObject for given dataObject id.
 *
 * @param {string} dataObjectId
 * @param {DataStructure} dataStructure
 *
 * @return {DataObjectProperties | undefined}
 */
export const getDataObject = ( dataObjectId : string, dataStructure : DataStructure ) : DataObjectProperties | undefined => {
    const recursiveLookup = ( innerDataStructure : DataStructure ) : DataObjectProperties | undefined => {
        let innerRetVal : DataObjectProperties | undefined;

        if ( innerDataStructure.id === dataObjectId ) {
            const dataObject = JSON.parse( JSON.stringify( innerDataStructure ) ) as DataStructure;

            delete dataObject.dataStructure;

            innerRetVal = dataObject;
        }
        else if ( innerDataStructure.dataStructure?.length ) {
            for ( const referencedDataStructure of innerDataStructure.dataStructure ) {
                innerRetVal = recursiveLookup( referencedDataStructure );

                if ( innerRetVal ) {
                    break;
                }
            }
        }

        return innerRetVal;
    };

    return recursiveLookup( dataStructure );
};

/**
 * Gets the dataObject for given dataObject id.
 *
 * @param {string} dataObjectId
 * @param {DataStructure} dataStructure
 *
 * @return {DataObjectProperties | undefined}
 */
export const getDataObjectOrig = ( dataObjectId : string, dataStructure : DataStructure ) : DataStructure | undefined => {
    const recursiveLookup = ( innerDataStructure : DataStructure ) : DataStructure | undefined => {
        let innerRetVal : DataStructure | undefined;

        if ( innerDataStructure.id === dataObjectId ) {
            innerRetVal = innerDataStructure;
        }
        else if ( innerDataStructure.dataStructure?.length ) {
            for ( const referencedDataStructure of innerDataStructure.dataStructure ) {
                innerRetVal = recursiveLookup( referencedDataStructure );

                if ( innerRetVal ) {
                    break;
                }
            }
        }

        return innerRetVal;
    };

    return recursiveLookup( dataStructure );
};

/**
 * Gets the dataStructure for given dataObject id including the reversed parent dataStructure leading to it.
 *
 * @param {string} dataObjectId
 * @param {DataStructure} dataStructure
 *
 * @return {DataObjectProperties | undefined}
 */
export const getDataStructure = ( dataObjectId : string, dataStructure : DataStructure ) : DataStructure | undefined => {
    const recursiveLookup = ( innerDataStructure : DataStructure, parentDataStructure? : DataStructure ) : DataStructure | undefined => {
        let innerRetVal : DataStructure | undefined;

        if ( innerDataStructure.id === dataObjectId ) {
            innerRetVal = JSON.parse( JSON.stringify( innerDataStructure ) ) as DataStructure;

            if ( parentDataStructure ) {
                if ( !innerRetVal.dataStructure ) {
                    innerRetVal.dataStructure = [
                        parentDataStructure
                    ];
                }
                else {
                    innerRetVal.dataStructure.push( parentDataStructure );
                }
            }
        }
        else if ( innerDataStructure.dataStructure?.length ) {
            for ( const referencedDataStructure of innerDataStructure.dataStructure ) {
                const parentReferencedDataStructure = JSON.parse( JSON.stringify( innerDataStructure ) ) as DataStructure;

                delete parentReferencedDataStructure.dataStructure;
                delete parentReferencedDataStructure.reference;
                delete parentReferencedDataStructure.referenceTarget;

                if ( parentDataStructure ) {
                    parentReferencedDataStructure.dataStructure = [ parentDataStructure ];
                }

                if ( referencedDataStructure.reference ) {
                    parentReferencedDataStructure.reference = {
                        id                    : referencedDataStructure.reference.id,
                        referenceConfiguration: {}
                    };

                    const referenceTypes = Object.keys( referencedDataStructure.reference.referenceConfiguration );

                    for ( const referenceType of referenceTypes ) {
                        const referenceConfiguration       = referencedDataStructure.reference.referenceConfiguration!,
                              parentReferenceConfiguration = parentReferencedDataStructure.reference.referenceConfiguration!;

                        parentReferenceConfiguration[ referenceType ] = { ...referenceConfiguration[ referenceType ] };

                        if ( referenceConfiguration[ referenceType ].referenceDirection === 'SrcToDest' ) {
                            parentReferenceConfiguration[ referenceType ].referenceDirection = 'DestToSrc';
                        }
                        else if ( referenceConfiguration[ referenceType ].referenceDirection === 'DestToSrc' ) {
                            parentReferenceConfiguration[ referenceType ].referenceDirection = 'SrcToDest';
                        }
                    }
                }

                if ( referencedDataStructure.referenceTarget ) {
                    parentReferencedDataStructure.referenceTarget = { ...referencedDataStructure.referenceTarget };

                    parentReferencedDataStructure.referenceTarget.nodeId = referencedDataStructure.referenceTarget.destNodeId;
                    parentReferencedDataStructure.referenceTarget.destNodeId = referencedDataStructure.referenceTarget.nodeId;

                    if ( referencedDataStructure.referenceTarget.referenceDirection === 'SrcToDest' ) {
                        parentReferencedDataStructure.referenceTarget.referenceDirection = 'DestToSrc';
                    }
                    else if ( referencedDataStructure.referenceTarget.referenceDirection === 'DestToSrc' ) {
                        parentReferencedDataStructure.referenceTarget.referenceDirection = 'SrcToDest';
                    }
                }

                innerRetVal = recursiveLookup( referencedDataStructure, parentReferencedDataStructure );

                if ( innerRetVal ) {
                    break;
                }
            }
        }

        return innerRetVal;
    };

    return recursiveLookup( dataStructure );
};

/**
 * Gets the dataObject for given dataObject origId.
 *
 * @param {number} dataObjectOrigId
 * @param {DataStructure} dataStructure
 *
 * @return {DataObjectProperties | undefined}
 */
export const getDataObjectByOrigId = ( dataObjectOrigId : number, dataStructure : DataStructure ) : DataObjectProperties | undefined => {
    const recursiveLookup = ( innerDataStructure : DataStructure ) : DataObjectProperties | undefined => {
        let innerRetVal : DataObjectProperties | undefined;

        if ( innerDataStructure.origId === dataObjectOrigId ) {
            const dataObject = JSON.parse( JSON.stringify( innerDataStructure ) ) as DataStructure;

            delete dataObject.dataStructure;

            innerRetVal = dataObject;
        }
        else if ( innerDataStructure.dataStructure?.length ) {
            for ( const referencedDataStructure of innerDataStructure.dataStructure ) {
                innerRetVal = recursiveLookup( referencedDataStructure );

                if ( innerRetVal ) {
                    break;
                }
            }
        }

        return innerRetVal;
    };

    return recursiveLookup( dataStructure );
};

/**
 * Gets the parent dataObject of the dataObject for given dataObject id.
 *
 * @param {string} dataObjectId
 * @param {DataStructure} dataStructure
 *
 * @return {ExtractedProperties | undefined}
 */
export const getParentDataObject = ( dataObjectId : string, dataStructure : DataStructure ) : DataObjectProperties | undefined => {
    const recursiveLookup = ( innerDataStructure : DataStructure ) : DataObjectProperties | undefined => {
        let innerRetVal : DataObjectProperties | undefined;

        if ( innerDataStructure.dataStructure?.length ) {
            for ( const referencedDataStructure of innerDataStructure.dataStructure ) {
                if ( referencedDataStructure.id === dataObjectId ) {
                    const dataObject = JSON.parse( JSON.stringify( innerDataStructure ) ) as DataStructure;

                    delete dataObject.dataStructure;

                    innerRetVal = dataObject;
                    break;
                }
                else {
                    innerRetVal = recursiveLookup( referencedDataStructure );

                    if ( innerRetVal ) {
                        break;
                    }
                }
            }
        }

        return innerRetVal;
    };

    return recursiveLookup( dataStructure );
};

/**
 * Gets the parent dataObjects of the dataObject for given dataObject id.
 *
 * @param {string} dataObjectId
 * @param {DataStructure} dataStructure
 *
 * @return {ExtractedProperties[] | undefined}
 */
export const getParentDataObjects = ( dataObjectId : string, dataStructure : DataStructure ) : DataObjectProperties[] => {
    const recursiveLookup = ( innerDataStructure : DataStructure ) : DataObjectProperties[] => {
        const innerRetVal : DataObjectProperties[] = [];

        if ( innerDataStructure.dataStructure?.length ) {
            for ( const referencedDataStructure of innerDataStructure.dataStructure ) {
                if ( referencedDataStructure.id === dataObjectId ) {
                    const dataObject = JSON.parse( JSON.stringify( innerDataStructure ) ) as DataStructure;

                    delete dataObject.dataStructure;

                    innerRetVal.push( dataObject );
                }

                innerRetVal.push( ...recursiveLookup( referencedDataStructure ) );
            }
        }

        return innerRetVal;
    };

    return recursiveLookup( dataStructure );
};

/**
 * Gets the path from the top down to the given dataObject within given dataStructure.
 *
 * @param {string} dataObjectId
 * @param {DataStructure} dataStructure
 *
 * @return {string[]}
 */
// TODO: a dataObject could have more than one parent!!!
//  -> get dataObject by id and then use origId to get all dataObjects and their parents
export const getDataStructurePath = ( dataObjectId : string, dataStructure : DataStructure ) : string[] => {
    const retVal = [ dataObjectId ] as string[];

    let parentDataObject : DataObjectProperties | undefined = getParentDataObject( dataObjectId, dataStructure );

    while ( parentDataObject ) {
        retVal.unshift( parentDataObject.id );
        parentDataObject = getParentDataObject( parentDataObject.id, dataStructure );
    }

    return retVal;
};

/**
 * Gets the ReferenceSet of the given dataObject within the given dataStructure
 *
 * @param {DataObjectProperties} dataObject
 * @param {DataStructure} dataStructure
 * @param {Schema} schema
 *
 * @return {ReferenceSet | undefined}
 */
export const getReferenceSetsForDataObject = ( dataObject : DataObjectProperties, dataStructure : DataStructure, schema : Schema ) : ReferenceSet[] => {
    const parentDataObject = getParentDataObject( dataObject.id, dataStructure ),
          itemType1        = String( dataObject.item.objectType ?? '' ),
          itemType2        = String( parentDataObject?.item.objectType ?? '' ),
          referenceTypes   = Object.keys( dataObject.reference?.referenceConfiguration ?? [] ) as string[];

    let retVal : ReferenceSet[] = [];

    for ( const referenceType of referenceTypes ) {
        let referenceDirection : 'src' | 'dest' | 'both' = 'both';

        if ( dataObject.reference!.referenceConfiguration![ referenceType ].referenceDirection === 'SrcToDest' ) {
            referenceDirection = 'src';
        }
        else if ( dataObject.reference!.referenceConfiguration![ referenceType ].referenceDirection === 'DestToSrc' ) {
            referenceDirection = 'dest';
        }

        if ( itemType1 && itemType2 && referenceType !== '*' ) {
            retVal = retVal.concat( metaModel[ schema ].getReferenceSetsForItemTypesAndReferenceType(
                itemType1,
                itemType2,
                referenceType,
                referenceDirection
            ) );
        }
        else if ( itemType1 && itemType2 && referenceType === '*' ) {
            retVal = retVal.concat( metaModel[ schema ].getReferenceSetsForItemTypes(
                itemType1,
                itemType2,
                referenceDirection
            ) );
        }
        else if ( itemType1 && !itemType2 && referenceType !== '*' ) {
            retVal = retVal.concat( metaModel[ schema ].getReferenceSetsForItemTypeAndReferenceType(
                itemType1,
                referenceType,
                referenceDirection
            ) );
        }
        else if ( !itemType1 && itemType2 && referenceType !== '*' ) {
            if ( referenceDirection === 'src' ) {
                referenceDirection = 'dest';
            }
            else if ( referenceDirection === 'dest' ) {
                referenceDirection = 'src';
            }

            retVal = retVal.concat( metaModel[ schema ].getReferenceSetsForItemTypeAndReferenceType(
                itemType2,
                referenceType,
                referenceDirection
            ) );
        }
        else if ( itemType1 && !itemType2 && referenceType === '*' ) {
            retVal = retVal.concat( metaModel[ schema ].getReferenceSetsForItemType(
                itemType1,
                referenceDirection
            ) );
        }
        else if ( !itemType1 && itemType2 && referenceType === '*' ) {
            if ( referenceDirection === 'src' ) {
                referenceDirection = 'dest';
            }
            else if ( referenceDirection === 'dest' ) {
                referenceDirection = 'src';
            }

            retVal = retVal.concat( metaModel[ schema ].getReferenceSetsForItemType(
                itemType2,
                referenceDirection
            ) );
        }
        else if ( !itemType1 && !itemType2 && referenceType !== '*' ) {
            retVal = retVal.concat( metaModel[ schema ].getReferenceSetsForReferenceType(
                referenceDirection
            ) );
        }
    }

    return retVal;
};

/**
 * Extracts all DataObjects contained in the given dataStructure.
 *
 * @param {DataStructure} dataStructure
 *
 * @return {DataObjectProperties[]}
 */
export const extractDataObjectsFromDataStructure = ( dataStructure : DataStructure ) : DataObjectProperties[] => {
    const retVal : DataObjectProperties[] = [];

    retVal.push( Object.keys( dataStructure ).reduce(
        ( innerRetVal, key ) => {
            if ( key !== 'dataStructure' ) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                innerRetVal[ key ] = dataStructure[ key ];
            }

            return innerRetVal;
        },
        {} as DataObjectProperties
    ) );

    if ( dataStructure.dataStructure?.length ) {
        for ( const dataObject of dataStructure.dataStructure ) {
            const subDataObjects = extractDataObjectsFromDataStructure( dataObject );

            for ( const subDataObject of subDataObjects ) {
                if ( !retVal.find( dOP => dOP.id === subDataObject.id ) ) {
                    retVal.push( subDataObject );
                }
            }
        }
    }

    return retVal;
};

/**
 * Extracts the ids of all DataObjects contained in the given dataStructure.
 *
 * @param {DataStructure} dataStructure
 *
 * @return {string[]}
 */
export const extractDataObjectIdsFromDataStructure = ( dataStructure : DataStructure ) : string[] => {
    let retVal : string[] = [ dataStructure.id ];

    if ( dataStructure.dataStructure?.length ) {
        for ( const subDataStructure of dataStructure.dataStructure ) {
            retVal = retVal.concat( extractDataObjectIdsFromDataStructure( subDataStructure ) );
        }
    }

    return retVal;
};

/**
 * Extracts the referenced DataObject for given item from given dataStructure and dataContext.
 *
 * @param {Item} item
 * @param {string} dataContext
 * @param {DataStructure} dataStructure
 * @param {string} referenceType
 * @param {'dest' | 'src'} referenceDirection
 *
 * @return {DataObjectProperties | undefined}
 */
export const extractDataObjectForItem = ( item : Item, dataContext : string, dataStructure : DataStructure, referenceType : string = 'providesDataFor', referenceDirection : 'dest' | 'src' = 'src' ) : DataObjectProperties | undefined => {
    let retVal : DataObjectProperties | undefined;

    for ( const itemReferenceId of item.references ) {
        const reference                                    = global.data[ dataContext ]?.references?.[ itemReferenceId ],
              dataObjectProvidesDataForItemReferenceTypeId = metaModel[ item.schema ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', item.itemTypeId, referenceType, referenceDirection )?.[ 0 ]?.id;

        if ( !reference.isDeleted() ) {
            if (
                referenceDirection === 'src'
                && reference?.destItemId === item.id
                && reference?.referenceSetId === dataObjectProvidesDataForItemReferenceTypeId
            ) {
                retVal = getDataObjectByOrigId( reference.srcItemId, dataStructure );
                break;
            }
            else if (
                referenceDirection === 'dest'
                && reference?.srcItemId === item.id
                && reference?.referenceSetId === dataObjectProvidesDataForItemReferenceTypeId
            ) {
                retVal = getDataObjectByOrigId( reference.destItemId, dataStructure );
                break;
            }
        }
    }

    return retVal;
};

/**
 * Extracts the dataStructure from the given one which only contains the given ids (all paths are dropped not leading to a given id)
 *
 * @param {DataStructure} dataStructure
 * @param {string[]} dataObjectIds
 *
 * @return {DataStructure | undefined}
 */
export const extractDataStructureForDataObjects = ( dataStructure : DataStructure, dataObjectIds : string[] ) : DataStructure | undefined => {
    const dataStructurePaths : string[][] = [];

    let retVal : DataStructure | undefined;

    // get the paths of the dataObjectIds within dataStructure
    for ( const dataObjectId of dataObjectIds ) {
        dataStructurePaths.push( getDataStructurePath( dataObjectId, dataStructure ) );
    }

    // build the dataStructure for paths
    if ( dataStructurePaths?.[ 0 ]?.[ 0 ] ) {
        const rootDataObject = getDataObject( dataStructurePaths[ 0 ][ 0 ], dataStructure ) as DataStructure;

        if ( rootDataObject ) {
            retVal = rootDataObject;

            for ( const usedPath of dataStructurePaths ) {
                let currentDataStructure = rootDataObject,
                    index                = 0;

                for ( const dataObjectId of usedPath ) {
                    index++;

                    // skip first element (root should be the same for all)
                    if ( index === 1 ) {
                        continue;
                    }

                    if ( !currentDataStructure.dataStructure ) {
                        currentDataStructure.dataStructure = [];
                    }

                    let dataObjectIndex = currentDataStructure.dataStructure.findIndex( dataObj => dataObj.id === dataObjectId );

                    if ( dataObjectIndex === -1 ) {
                        const dataObject = getDataObject( dataObjectId, dataStructure ) as DataStructure;

                        if ( dataObject ) {
                            currentDataStructure.dataStructure.push( dataObject );
                            dataObjectIndex = currentDataStructure.dataStructure.length - 1;
                        }
                    }

                    if ( dataObjectIndex !== -1 ) {
                        currentDataStructure = currentDataStructure.dataStructure[ dataObjectIndex ];
                    }
                    // no dataObject -> break path (should never happen)
                    else {
                        break;
                    }
                }
            }
        }
    }

    return retVal;
};

/**
 * Builds the DataDescriptor for given data and dataObject.
 *
 * @param {DataDescriptorBuildParams} data
 * @param {DataObjectProperties} dataObject
 * @param {boolean} [simpleDescriptor=false] set to true for simple alias like Key, Name, <PropertyName> (used by tree) *
 * @return {DataDescriptor | undefined}
 */
export const buildDataDescriptor = ( data : DataDescriptorBuildParams, dataObject : DataObjectProperties | DataObjectProperties[], simpleDescriptor : boolean = false ) : DataDescriptor | undefined => {
    let retVal : DataDescriptor | undefined;

    // attribute path dataDescriptor
    if ( data.attributePath ) {
        const attributePaths = data.attributePath
                .match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu )
            ?? [ data.attributePath ];

        // single field attribute path -> ObjectDataDescriptor
        if ( attributePaths.length === 1 && attributePaths[ 0 ] === data.attributePath ) {
            let attributePathDataObject : DataObjectProperties | undefined;

            // single dataObject
            if ( !Array.isArray( dataObject ) ) {
                attributePathDataObject = dataObject;
            }

            // multiple dataObjects (find right one)
            else {
                const attributePathParts = attributePaths[ 0 ].trim().split( '.' ),
                      dataObjectKey      = attributePathParts.shift();

                attributePathDataObject = dataObject.find( dO => dO.item.key === dataObjectKey );

                if ( !attributePathDataObject ) {
                    attributePathDataObject = dataObject[ 0 ];
                }
            }

            if ( attributePathDataObject ) {
                retVal = getObjectDataDescriptor( data, attributePathDataObject, simpleDescriptor );
            }
        }

        // expression attribute path -> ExpressionDataDescriptor
        else {
            const dataDescriptors : ObjectDataDescriptor[] = [],
                  fields : string[]                        = [],
                  pathMappings : {
                      [ attributePath : string ] : string
                  }                                        = {};

            for ( const attributePathRaw of attributePaths ) {
                let attributePath = attributePathRaw.trim(),
                    attributePathDataObject : DataObjectProperties | undefined;

                if ( !Array.isArray( dataObject ) ) {
                    attributePathDataObject = dataObject
                }
                else {
                    const attributePathParts = attributePath.split( '.' ),
                          dataObjectKey      = attributePathParts.shift();

                    attributePath = attributePathParts.join( '.' );
                    attributePathDataObject = dataObject.find( dO => dO.item.key === dataObjectKey );
                }

                const columnDataDescriptor = getObjectDataDescriptor( attributePath, attributePathDataObject );

                if ( columnDataDescriptor ) {
                    dataDescriptors.push( columnDataDescriptor );
                    fields.push( columnDataDescriptor.field );

                    pathMappings[ attributePathRaw.trim() ] = columnDataDescriptor.field;
                }
            }

            if ( dataDescriptors.length ) {
                const aliasIndex = data.attributePath.indexOf( ' as ' );

                let alias : string = '',
                    field : string = '';

                if ( aliasIndex !== -1 ) {
                    alias = data.attributePath.substring( aliasIndex + 4 ).trim().replaceAll( '"', '' ).replaceAll( '\'', '' );
                    field = data.attributePath.substring( 0, aliasIndex ).trim();
                }
                else {
                    field = data.attributePath;
                }

                for ( const attributePath of Object.keys( pathMappings ) as string[] ) {
                    const regExp = new RegExp( `(^|\\W)${attributePath.replace( '.', '\\.' )}($|\\W)`, 'g' );

                    field = field.replaceAll( regExp, `$1${pathMappings[ attributePath ]}$2` );

                    const hash = crypto.createHash( 'sha1' ).update( field ).digest( 'base64' ).replace( /\W/g, '' );

                    alias = `expression_${hash.substring( hash.length - 9 )}`;

                    if ( data.aggregateFunction ) {
                        alias = `${data.aggregateFunction === 'count(*)' ? 'countAll' : data.aggregateFunction}_${alias}`;
                    }
                }

                if ( simpleDescriptor ) {
                    alias = data.attributePath.split( '.' ).pop() ?? alias;
                }

                retVal = {
                    type           : 'expression',
                    alias,
                    fullAlias      : dataDescriptors.map( dD => dD.alias ).join( ',' ),
                    field,
                    fields,
                    dataDescriptors: dataDescriptors,
                    aggregate      : data.aggregateFunction,
                    attributePath  : data.attributePath
                };
            }
        }
    }

    // query field dataDescriptor -> StaticDataDescriptor
    else if ( data.queryFieldName && !Array.isArray( dataObject ) ) {
        retVal = {
            type      : 'static',
            alias     : data.queryFieldName,
            fullAlias : data.queryFieldName,
            dataObject: dataObject,
            field     : data.queryFieldName,
            valueType : data.valueType ?? 'STRING'
        };
    }

    // limit alias to 64 chars
    if ( retVal?.alias && retVal.alias.length > 63 ) {
        let hash = crypto.createHash( 'sha1' ).update( retVal.alias ).digest( 'base64' ).replace( /\W/g, '' );

        hash = `hashed_${hash.substring( hash.length - 8 )}`;

        retVal.alias = `${retVal.alias.substring( 0, Math.min( retVal.alias.indexOf( aliasSeparator ), ( 63 - aliasSeparator.length - hash.length ) ) )}${aliasSeparator}${hash}`;
    }

    return retVal;
};

/**
 * Gets the DataDescriptor.
 *
 * @param {string | Pick<DataDescriptorBuildParams, 'attributePath' | 'aggregateFunction'>} config
 * @param {DataObjectProperties} dataObject
 * @param {boolean} [simpleDescriptor=false] set to true for simple alias like Key, Name, <PropertyName> (used by tree)
 *
 * @return {DataDescriptor | undefined}
 */
export const getObjectDataDescriptor = ( config? : string | Pick<DataDescriptorBuildParams, 'attributePath' | 'aggregateFunction'>, dataObject? : DataObjectProperties, simpleDescriptor : boolean = false ) : ObjectDataDescriptor | undefined => {
    let retVal : ObjectDataDescriptor | undefined;

    const attributePath = typeof config === 'string' ? config : config?.attributePath,
          aggregate     = typeof config === 'string' ? undefined : config?.aggregateFunction;

    if ( attributePath && dataObject ) {
        const attributePathParts = attributePath.split( '.' );

        let attribute,
            arrayElementsSelector : 'every' | 'some' = 'every',
            dataObjectLabel                          = '',
            alias                                    = '',
            fullAlias                                = '',
            fieldLabel                               = '',
            attributePathPartsIndex                  = 0;

        if ( attributePathParts.length > 1 ) {
            if (
                attributePathParts[ 0 ] === 'item'
                || attributePathParts[ 0 ] === 'items'
                || attributePathParts[ 0 ] === 'property'
                || attributePathParts[ 0 ] === 'properties'
                || (
                    attributePathParts[ 0 ] === 'reference'
                    && (
                        attributePathParts[ 1 ] === 'referenceTypeNameWithDirection'
                        || attributePathParts[ 1 ] === 'referenceEnd'
                    )
                )
            ) {
                dataObjectLabel = getItemLabelForCypher( dataObject );
                fieldLabel = getItemLabelForCypher( dataObject );
            }
            else {
                const referenceTypes = Object.keys( dataObject.reference?.referenceConfiguration ?? [] ) as string[];

                let referenceAliasLabel = '',
                    referenceFieldLabel : string | undefined;

                if ( !referenceTypes.length || referenceTypes.length === 1 ) {
                    referenceAliasLabel = getReferenceLabelForCypher( dataObject );
                    referenceFieldLabel = getReferenceLabelForCypher( dataObject );
                }
                else if ( metaModel[ schema ].getReferenceType( referenceTypes[ 0 ] )?.name ) {
                    referenceAliasLabel = getReferenceLabelForCypher( dataObject, `_${metaModel[ schema ].getReferenceType( referenceTypes[ 0 ] )?.name}` );
                    referenceFieldLabel = getReferenceLabelForCypher( dataObject, `_${referenceTypes[ 0 ]}` );
                }
                else {
                    referenceAliasLabel = getReferenceLabelForCypher( dataObject, `_${referenceTypes[ 0 ]}` );
                    referenceFieldLabel = getReferenceLabelForCypher( dataObject, `_${referenceTypes[ 0 ]}` );
                }

                dataObjectLabel = `${referenceAliasLabel}`;
                fieldLabel = `${referenceFieldLabel}`;
            }

            alias = `${dataObjectLabel}${aliasSeparator}${attributePathParts[ attributePathPartsIndex++ ]}${aliasSeparator}${attributePathParts[ attributePathPartsIndex++ ]}`;

            if (
                attributePathParts[ 0 ] === 'items'
                || attributePathParts[ 0 ] === 'properties'
                || attributePathParts[ 0 ] === 'references'
                || attributePathParts[ 0 ] === 'referenceProperties'
            ) {
                alias += `${aliasSeparator}${attributePathParts[ attributePathPartsIndex++ ]}`;
            }

            if (
                attributePathParts[ 0 ] === 'property'
                || attributePathParts[ 0 ] === 'properties'
                || attributePathParts[ 0 ] === 'referenceProperty'
                || attributePathParts[ 0 ] === 'referenceProperties'
            ) {
                alias += `${aliasSeparator}${attributePathParts[ attributePathPartsIndex++ ] ?? 'value'}`;
            }

            fullAlias = alias;

            if ( aggregate ) {
                const hash = crypto.createHash( 'sha1' ).update( alias ).digest( 'base64' ).replace( /\W/g, '' );

                alias = `${dataObjectLabel}${aliasSeparator}${aggregate === 'count(*)' ? 'countAll' : aggregate}_${hash.substring( hash.length - 9 )}`;
            }

            if ( simpleDescriptor ) {
                alias = attributePath.split( fieldSeparator ).pop() ?? alias;
            }
            else {
                alias = getAliasFromAttributePath( attributePath ) ?? alias;
            }

            // limit alias to 64 chars
            if ( alias.length > 63 ) {
                let hash = crypto.createHash( 'sha1' ).update( alias ).digest( 'base64' ).replace( /\W/g, '' );

                hash = `hashed_${hash.substring( hash.length - 8 )}`;

                alias = `${alias.substring( 0, Math.min( alias.indexOf( aliasSeparator ), ( 63 - aliasSeparator.length - hash.length ) ) )}${aliasSeparator}${hash}`;
            }

            switch ( attributePathParts[ 0 ] ) {
                case 'item':
                    attribute = attributePathParts[ 1 ] as ItemAttributesDataDescriptor;

                    retVal = {
                        type      : 'item',
                        attribute,
                        alias,
                        fullAlias,
                        dataObject: dataObject,
                        label     : fieldLabel,
                        field     : `${fieldLabel}${fieldSeparator}item${_.upperFirst( remapColumnDataDescriptorItemAttributes( attribute ) )}`,
                        aggregate,
                        attributePath
                    };
                    break;

                case 'items':
                    arrayElementsSelector = attributePathParts[ 1 ] as ArrayElementsSelector;
                    attribute = attributePathParts[ 2 ] as ItemAttributesDataDescriptor;

                    retVal = {
                        type      : 'items',
                        arrayElementsSelector,
                        attribute,
                        alias,
                        fullAlias,
                        dataObject: dataObject,
                        label     : fieldLabel,
                        field     : `${fieldLabel}${fieldSeparator}item${_.upperFirst( remapColumnDataDescriptorItemAttributes( attribute ) )}`,
                        aggregate,
                        attributePath
                    };
                    break;

                case 'property':
                    attribute = ( attributePathParts[ 2 ] ?? 'value' ) as PropertyAttributesDataDescriptor;

                    retVal = {
                        type            : 'property',
                        attribute,
                        propertyTypeName: attributePathParts[ 1 ],
                        alias,
                        fullAlias,
                        dataObject      : dataObject,
                        label           : fieldLabel,
                        field           : `${fieldLabel}${fieldSeparator}${attributePathParts[ 1 ]}${fieldSeparator}${remapColumnDataDescriptorPropertyAttributes( attribute )}`,
                        aggregate,
                        attributePath
                    };
                    break;

                case 'properties':
                    arrayElementsSelector = attributePathParts[ 1 ] as ArrayElementsSelector;
                    attribute = ( attributePathParts[ 3 ] ?? 'value' ) as PropertyAttributesDataDescriptor;

                    retVal = {
                        type            : 'properties',
                        arrayElementsSelector,
                        attribute,
                        propertyTypeName: attributePathParts[ 2 ],
                        alias,
                        fullAlias,
                        dataObject      : dataObject,
                        label           : fieldLabel,
                        field           : `${fieldLabel}${fieldSeparator}${attributePathParts[ 2 ]}${fieldSeparator}${remapColumnDataDescriptorPropertyAttributes( attribute )}`,
                        aggregate,
                        attributePath
                    };
                    break;

                case 'reference':
                    attribute = attributePathParts[ 1 ] as ReferenceAttributesDataDescriptor;

                    retVal = {
                        type      : 'reference',
                        attribute,
                        alias,
                        fullAlias,
                        dataObject: dataObject,
                        label     : fieldLabel,
                        field     : attribute !== 'referenceEnd' && attribute !== 'referenceTypeNameWithDirection'
                            ? `${fieldLabel}${fieldSeparator}reference${_.upperFirst( remapColumnDataDescriptorReferenceAttributes( attribute ) )}`
                            : `${fieldLabel}${fieldSeparator}itemId`,
                        aggregate,
                        attributePath
                    };
                    break;

                case 'references':
                    arrayElementsSelector = attributePathParts[ 1 ] as ArrayElementsSelector;
                    attribute = attributePathParts[ 2 ] as ReferenceAttributesDataDescriptor;

                    retVal = {
                        type      : 'references',
                        arrayElementsSelector,
                        attribute,
                        alias,
                        fullAlias,
                        dataObject: dataObject,
                        label     : fieldLabel,
                        field     : attribute !== 'referenceEnd' && attribute !== 'referenceTypeNameWithDirection'
                            ? `${fieldLabel}${fieldSeparator}reference${_.upperFirst( remapColumnDataDescriptorReferenceAttributes( attribute ) )}`
                            : `${fieldLabel}${fieldSeparator}itemId`,
                        aggregate,
                        attributePath
                    };
                    break;

                case 'referenceProperty':
                    attribute = ( attributePathParts[ 2 ] ?? 'value' ) as ReferencePropertyAttributesDataDescriptor;

                    retVal = {
                        type            : 'referenceProperty',
                        attribute,
                        propertyTypeName: attributePathParts[ 1 ],
                        alias,
                        fullAlias,
                        dataObject      : dataObject,
                        label           : fieldLabel,
                        field           : `${fieldLabel}${fieldSeparator}${attributePathParts[ 1 ]}${fieldSeparator}${remapColumnDataDescriptorReferencePropertyAttributes( attribute )}`,
                        aggregate,
                        attributePath
                    };
                    break;

                case 'referenceProperties':
                    arrayElementsSelector = attributePathParts[ 1 ] as ArrayElementsSelector;
                    attribute = ( attributePathParts[ 3 ] ?? 'value' ) as ReferencePropertyAttributesDataDescriptor;

                    retVal = {
                        type            : 'referenceProperties',
                        arrayElementsSelector,
                        attribute,
                        propertyTypeName: attributePathParts[ 2 ],
                        alias,
                        fullAlias,
                        dataObject      : dataObject,
                        label           : fieldLabel,
                        field           : `${fieldLabel}${fieldSeparator}${attributePathParts[ 2 ]}${fieldSeparator}${remapColumnDataDescriptorReferencePropertyAttributes( attribute )}`,
                        aggregate,
                        attributePath
                    };
                    break;
            }
        }
    }

    return retVal;
};


export const aliasSeparator = ':';
export const fieldSeparator = '.';
const getAliasFromAttributePath = ( attributePath : string ) : string | undefined => {
    const aliasIndex = ( attributePath ?? '' ).indexOf( ' as ' );

    let retVal : string | undefined;

    // attribute path contains alias ( ... as ...) -> use it
    if ( aliasIndex !== -1 ) {
        retVal = attributePath!.substring( aliasIndex + 4 ).trim().replaceAll( '"', '' ).replaceAll( '\'', '' );
    }

    return retVal;
};


/**
 * Builds a label usable for items in cypher queries.
 *
 * @param {DataStructure} dataObject
 *
 * @return {string}
 */
export const getItemLabelForCypher = ( dataObject : DataStructure ) : string => {
    return buildItemLabelForCypher( dataObject.item.key );
};
export const buildItemLabelForCypher = ( label : string ) : string => {
    return `item_${label.replace( /\W/g, '' )}`;
};

/**
 * Builds a label usable for references in cypher queries.
 *
 * @param {DataStructure} dataObject
 * @param {string} suffix
 *
 * @return {string}
 */
export const getReferenceLabelForCypher = ( dataObject : DataStructure, suffix : string = '' ) : string => {
    return buildReferenceLabelForCypher( dataObject.item.key + suffix );
};
export const buildReferenceLabelForCypher = ( label : string ) : string => {
    return `reference_${label.replace( /\W/g, '' )}`.replace( /_$/g, '' );
};


/**
 * Remaps virtual Item attributes to a real one.
 *
 * @param {ItemAttributesDataDescriptor} attribute
 *
 * @return {ItemAttributesDataDescriptor}
 */
const remapColumnDataDescriptorItemAttributes = ( attribute : ItemAttributesDataDescriptor ) : ItemAttributesDataDescriptor => {
    let retVal : ItemAttributesDataDescriptor = attribute;

    if ( attribute === 'itemTypeName' ) {
        retVal = 'itemTypeId';
    }
    else if ( attribute === 'statusName' ) {
        retVal = 'statusId';
    }

    return retVal;
};

/**
 * Remaps virtual Property attributes to a real one.
 *
 * @param {PropertyAttributesDataDescriptor} attribute
 *
 * @return {PropertyAttributesDataDescriptor}
 */
const remapColumnDataDescriptorPropertyAttributes = ( attribute : PropertyAttributesDataDescriptor ) => {
    let retVal : PropertyAttributesDataDescriptor = attribute;

    if ( attribute === 'propertySetName' ) {
        retVal = 'propertySetId';
    }
    else if ( attribute === 'propertyTypeName' ) {
        retVal = 'propertySetId';
    }
    else if ( attribute === 'statusName' ) {
        retVal = 'statusId';
    }

    return retVal;
};

/**
 * Remaps virtual Reference attributes to a real one.
 *
 * @param {ReferenceAttributesDataDescriptor} attribute
 *
 * @return {ReferenceAttributesDataDescriptor}
 */
const remapColumnDataDescriptorReferenceAttributes = ( attribute : ReferenceAttributesDataDescriptor ) => {
    let retVal : ReferenceAttributesDataDescriptor = attribute;

    if ( attribute === 'referenceSetName' ) {
        retVal = 'referenceSetId';
    }
    else if ( attribute === 'referenceTypeName' ) {
        retVal = 'referenceSetId';
    }
    else if ( attribute === 'statusName' ) {
        retVal = 'statusId';
    }

    return retVal;
};

/**
 * Remaps virtual ReferenceProperty attributes to a real one.
 *
 * @param {ReferencePropertyAttributesDataDescriptor} attribute
 *
 * @return {ReferencePropertyAttributesDataDescriptor}
 */
const remapColumnDataDescriptorReferencePropertyAttributes = ( attribute : ReferencePropertyAttributesDataDescriptor ) => {
    let retVal : ReferencePropertyAttributesDataDescriptor = attribute;

    if ( attribute === 'referencePropertySetName' ) {
        retVal = 'referencePropertySetId';
    }
    else if ( attribute === 'propertyTypeName' ) {
        retVal = 'referencePropertySetId';
    }
    else if ( attribute === 'statusName' ) {
        retVal = 'statusId';
    }

    return retVal;
};

export const dataStructureVisualizer = ( dataStructure : DataStructure, level : number = 0 ) : string => {
    let retVal : string = '';

    // referenced childs
    if ( dataStructure.reference?.referenceConfiguration ) {
        const referenceTypes = Object.keys( dataStructure.reference.referenceConfiguration ) as string[];
        for ( const referenceType of referenceTypes ) {
            const referenceDirection    = dataStructure.reference.referenceConfiguration[ referenceType ]?.referenceDirection ?? '*',
                  referenceIsMandatory  = !dataStructure.reference.referenceConfiguration[ referenceType ]?.referenceIsMandatory ?? false,
                  referenceRecursionMin = dataStructure.reference.referenceConfiguration[ referenceType ]?.referenceRecursionMin ?? 1,
                  referenceRecursionMax = dataStructure.reference.referenceConfiguration[ referenceType ]?.referenceRecursionMin ?? 1;

            retVal += !referenceIsMandatory ? '( ' : '';
            retVal += `${referenceDirection === 'SrcToDest' ? '<' : ''}- `;
            retVal += referenceType ? referenceType : '*';
            if ( referenceRecursionMin > 1 || referenceRecursionMin !== referenceRecursionMax ) {
                retVal += ` {${referenceRecursionMin}, ${referenceRecursionMax}}`;
            }
            retVal += ` -${referenceDirection === 'DestToSrc' ? '>' : ''}`;
            retVal += `${!referenceIsMandatory ? ' )' : ''} `;
        }
    }

    retVal += dataStructure.item.objectType ? dataStructure.item.objectType : '*';

    retVal = retVal.indent( level );

    if ( dataStructure.dataStructure?.length ) {
        for ( const childDataStructure of dataStructure.dataStructure ) {
            retVal += `\r\n${dataStructureVisualizer( childDataStructure, level + 1 )}`;
        }
    }

    return retVal;
};
